/**
 * Hermes Loyalty Helper
 * Funciones globales para acceder a los datos del programa de lealtad.
 *
 * Auth: usa el App Proxy de Shopify (mismo origen). Las peticiones a `/apps/loyalty`
 * las firma Shopify y agrega `logged_in_customer_id`; NO se envía bearer token y el
 * backend obtiene el email/teléfono del cliente desde Shopify. Por eso refreshData ya
 * no requiere `customer` ni session token.
 */
window.HermesLoyalty = (function() {
  const STORAGE_KEY = 'hermesLoyaltyData';
  const SESSION_TOKEN_KEY = 'sessionLoyalty';
  const DEFAULT_MAX_AGE_MINUTES = 60;

  // Ruta del App Proxy (mismo origen). Se puede sobrescribir desde Liquid con setApiUrl.
  let API_URL = '/apps/loyalty';

  /**
   * Configura la URL de la API
   * @param {string} url
   */
  function setApiUrl(url) {
    if (url) API_URL = url;
  }

  /**
   * Obtiene datos de lealtad desde localStorage
   * @returns {Object|null}
   */
  function getData() {
    const data = localStorage.getItem(STORAGE_KEY);
    if (!data) return null;

    try {
      return JSON.parse(data);
    } catch (e) {
      console.warn('[HermesLoyalty] Error parsing stored data:', e);
      return null;
    }
  }

  /**
   * Guarda datos de lealtad en localStorage
   * @param {Object} data
   */
  function setData(data) {
    try {
      data.lastUpdated = Date.now();
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));

      // Disparar evento para que otros componentes se actualicen
      window.dispatchEvent(new CustomEvent('loyaltyDataUpdated', { detail: data }));
    } catch (e) {
      console.warn('[HermesLoyalty] Error saving data:', e);
    }
  }

  /**
   * Verifica si los datos son recientes
   * @param {number} maxAgeMinutes - Máxima edad en minutos (default: 60)
   * @returns {boolean}
   */
  function isDataFresh(maxAgeMinutes = DEFAULT_MAX_AGE_MINUTES) {
    const data = getData();
    if (!data || !data.lastUpdated) return false;

    const ageInMinutes = (Date.now() - data.lastUpdated) / (1000 * 60);
    return ageInMinutes < maxAgeMinutes;
  }

  /**
   * Obtiene el token de sesión
   * @returns {string|null}
   */
  function getSessionToken() {
    return sessionStorage.getItem(SESSION_TOKEN_KEY);
  }

  /**
   * Guarda el token de sesión
   * @param {string} token
   */
  function setSessionToken(token) {
    sessionStorage.setItem(SESSION_TOKEN_KEY, token);
  }

  /**
   * Refresca los datos desde el App Proxy.
   * @param {Object} [customer] - Ignorado (compat). El backend identifica al cliente vía App Proxy.
   * @param {boolean} force - Forzar actualización aunque los datos sean frescos
   * @returns {Promise<Object|null>}
   */
  async function refreshData(customer, force = false) {
    // Solo hacer fetch si los datos son viejos o no existen
    if (!force && isDataFresh()) {
      return getData();
    }

    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // Mismo origen: se envían las cookies de sesión del storefront para que
        // Shopify resuelva logged_in_customer_id en el App Proxy.
        credentials: 'same-origin',
        body: JSON.stringify({ _action: 'data' })
      });

      if (!response.ok) {
        // 401/403: cliente no logueado o no miembro; no es un error fatal.
        if (response.status === 401 || response.status === 403) {
          return null;
        }
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      if (data.success && data.customer && data.customer.valor) {
        const { nivel, puntosDisponibles, puntosRedimidos, puntosCodigos, fechaAfiliacion } = data.customer.valor;

        const loyaltyData = {
          nivel: {
            clave: nivel?.clave || 'BASICO',
            descripcion: nivel?.descripcion || 'Básico'
          },
          puntosDisponibles: puntosDisponibles || 0,
          puntosRedimidos: puntosRedimidos || 0,
          puntosCodigos: puntosCodigos || [],
          fechaAfiliacion: fechaAfiliacion || null,
          lastUpdated: Date.now()
        };

        setData(loyaltyData);
        return loyaltyData;
      } else {
        console.warn('[HermesLoyalty] API returned unsuccessful response:', data);
      }
    } catch (error) {
      console.error('[HermesLoyalty] Error refreshing data:', error);
    }

    return null;
  }

  /**
   * Obtiene los puntos disponibles
   * @returns {number}
   */
  function getPoints() {
    const data = getData();
    return data ? Math.trunc(data.puntosDisponibles || 0) : 0;
  }

  /**
   * Obtiene el nivel del cliente
   * @returns {Object} { clave, descripcion }
   */
  function getLevel() {
    const data = getData();
    return data?.nivel || { clave: 'BASICO', descripcion: 'Básico' };
  }

  /**
   * Obtiene los puntos formateados para mostrar
   * @returns {string}
   */
  function getFormattedPoints() {
    return getPoints().toLocaleString('es-MX');
  }

  /**
   * Limpia los datos de lealtad (logout)
   */
  function clearData() {
    localStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(SESSION_TOKEN_KEY);
    window.dispatchEvent(new CustomEvent('loyaltyDataCleared'));
  }

  /**
   * Verifica si hay datos disponibles
   * @returns {boolean}
   */
  function hasData() {
    return getData() !== null;
  }

  // API pública
  return {
    setApiUrl,
    getData,
    setData,
    isDataFresh,
    getSessionToken,
    setSessionToken,
    refreshData,
    getPoints,
    getLevel,
    getFormattedPoints,
    clearData,
    hasData,
    // Constantes
    STORAGE_KEY,
    SESSION_TOKEN_KEY
  };
})();
